import {
	CloseReading,
	ClozeDropdown,
	FreeResponse,
	ImageLabeling,
	MultipleChoice,
	MultipleSelect,
	Question,
	QuestionChoice,
	MediaObj,
	StudentAnswer,
	DropdownAnswer,
	ValidationResult,
	ClientQuestion,
	ClassScore,
	ClientFields,
	TemplateSegment,
} from '@mission.io/question-toolkit'
import { validateMedia, isString, isLikeObject } from './validate'
import { CLOSE_READING } from './constants'
import { Root } from 'mdast'
import { getMediaSchema, required, requiredNonEmptyArray } from './model'
import { Schema as MongooseSchema } from 'mongoose'
import {
	buildWordRangeTransformer,
	gradeAnnotations,
} from './helpers/annotations'

export enum QuestionVariant {
	CLOSE_READING = 'CLOSE_READING',
	CLOZE_DROPDOWN = 'CLOZE_DROPDOWN',
	FREE_RESPONSE = 'FREE_RESPONSE',
	IMAGE_LABELING = 'IMAGE_LABELING',
	MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
	MULTIPLE_SELECT = 'MULTIPLE_SELECT',
}

export enum MediaType {
	IMAGE = 'IMAGE',
}

export enum TemplateSegmentType {
	'Text' = 'Text',
	'Input' = 'Input',
}

/**
 * parseTemplateStringIntoSegments - parses the template string into segments (the inverse of convertTemplateSegmentsToTemplateString)
 *
 * @param  {string} template - the template string to parse
 *
 * @returns TemplateSegment[] - the segments in the template, each segment will either be of type TemplateSegmentType.Input or TemplateSegmentType.Text
 */
export function parseTemplateStringIntoSegments(
	template: string
): TemplateSegment[] {
	const segments: TemplateSegment[] = []
	let startingIndex = 0
	for (const match of template.matchAll(/\{\{response\[\d+\]\}\}/g)) {
		// for any match retrieved from matchAll, the first element is the matching string
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const inputString = match[0]!
		const matchStart = match.index
		const textString = template.substring(startingIndex, matchStart)
		if (textString) {
			segments.push({
				type: TemplateSegmentType.Text,
				text: textString,
			})
		}
		if (inputString.length) {
			const inputId = inputString.match(/\d+/)?.[0]
			if (inputId?.length) {
				segments.push({ type: TemplateSegmentType.Input, inputId })
			}
		}
		startingIndex = (matchStart ?? 0) + inputString.length
	}
	if (startingIndex !== template.length) {
		segments.push({
			type: TemplateSegmentType.Text,
			text: template.substring(startingIndex, template.length),
		})
	}
	return segments
}

/**
 * convertTemplateSegmentsToTemplateString - convert an array of template segments into a template string (the inverse of parseTemplateStringIntoSegments)
 *
 * @param  {TemplateSegment[]} templateSegments - the template segments to convert into a template string
 *
 * @returns string - the template string representing the template segments
 */
export function convertTemplateSegmentsToTemplateString(
	templateSegments: TemplateSegment[]
): string {
	return templateSegments
		.map(segment => {
			if (segment.type === TemplateSegmentType.Text) {
				return segment.text
			}
			if (segment.type === TemplateSegmentType.Input) {
				return `{{response[${segment.inputId}]}}`
			}
			return ''
		})
		.join('')
}

/**
 * Returns an ordered list of the template response keys occurring in the the template
 * @param {string} template
 * @returns {string[]}
 */
export function getTemplateKeys(template: string): string[] {
	const inputIds: string[] = []
	parseTemplateStringIntoSegments(template).forEach(segment => {
		if (segment.type === TemplateSegmentType.Input) {
			inputIds.push(segment.inputId)
		}
	})
	return inputIds
}

let closeReadingSchema: MongooseSchema<Omit<CloseReading, 'type'>> | undefined

export const QuestionHelpers = {
	[QuestionVariant.CLOSE_READING]: {
		getSchema: (
			Schema: typeof MongooseSchema
		): MongooseSchema<Omit<CloseReading, 'type'>> => {
			if (closeReadingSchema) {
				return closeReadingSchema
			}
			const AnnotationToolSchema = new Schema(
				{
					type: required(String, {
						enum: CLOSE_READING.ANNOTATION_TOOL,
					}),
					colors: [{ type: String, enum: CLOSE_READING.COLOR }],
				},
				{ _id: false }
			)
			const StyleSchema = new Schema(
				{
					tool: {
						type: String,
						enum: [...Object.keys(CLOSE_READING.ANNOTATION_TOOL), null],
						required: false,
					},
					color: {
						type: String,
						enum: [...Object.keys(CLOSE_READING.COLOR), null],
						required: false,
					},
				},
				{ _id: false }
			)
			const TargetAnnotationSchema = new Schema(
				{
					score: required(Number),
					required: required(Boolean),
					range: required({
						start: required(Number),
						end: required(Number),
					}),
					buffer: required({
						start: required(Number),
						end: required(Number),
					}),
					style: required(StyleSchema),
				},
				{ _id: false }
			)
			const GradingSchema = new Schema(
				{
					type: required(String, { enum: CLOSE_READING.GRADING }),
					requiredScore: Number,
				},
				{ _id: false }
			)
			closeReadingSchema = new Schema(
				{
					phrase: required(String),
					textBody: required(String),
					annotationLimit: Number,
					requiredAnnotations: Number,
					grading: required(GradingSchema),
					matchType: required(String, { enum: CLOSE_READING.MATCH_TYPE }),
					availableAnnotationTools: requiredNonEmptyArray(
						AnnotationToolSchema,
						'Available annotation tools must be provided'
					),
					targetAnnotations: [TargetAnnotationSchema],
				},
				{ _id: false }
			)
			return closeReadingSchema
		},
		grade: (
			question: Question,
			answer: StudentAnswer,
			options?: { markdownAST?: Root }
		): number | undefined => {
			if (question.type !== QuestionVariant.CLOSE_READING) return
			if (answer.type !== QuestionVariant.CLOSE_READING) return
			if (question.grading.type === CLOSE_READING.GRADING.NONE) return
			const { annotations: studentAnnotations } = answer.value
			let rangeTransformer
			if (question.matchType === CLOSE_READING.MATCH_TYPE.WORD) {
				if (!options?.markdownAST) {
					throw new Error(
						'Missing markdownAST for word range transformer in close reading question'
					)
				}
				rangeTransformer = buildWordRangeTransformer(options.markdownAST)
			} else {
				throw new Error('Unsupported match type for close reading question')
			}
			// Ensure student met the requirements for number of annotations
			if (
				(question.requiredAnnotations &&
					question.requiredAnnotations > studentAnnotations.length) ||
				(question.annotationLimit &&
					question.annotationLimit < studentAnnotations.length)
			) {
				return 0
			}
			const { score, allRequiredTargetsSatisfied } = gradeAnnotations(
				question.targetAnnotations,
				studentAnnotations,
				rangeTransformer
			)
			if (!allRequiredTargetsSatisfied) {
				return 0
			}
			if (
				question.grading.type === CLOSE_READING.GRADING.PASS_FAIL &&
				question.grading.requiredScore > score
			) {
				return 0
			}
			return score
		},
		getBestPossibleScore: (question: CloseReading): number => {
			if (question.grading.type === CLOSE_READING.GRADING.PARTIAL_CREDIT) {
				return question.targetAnnotations.reduce(
					(total, targetAnnotation) => (total += targetAnnotation.score),
					0
				)
				// return question.grading.fullCreditScore // TODO: potentially allow a full credit score to be set for additional flexibility.
				// I.E if we only want to require 3 annotations but define 5 target annotations with a score of 1, then the full credit score could be 3
			} else if (question.grading.type === CLOSE_READING.GRADING.PASS_FAIL) {
				return question.grading.requiredScore
			}
			return 1
		},
		// Validate a close reading question
		validate: (
			question: Partial<Record<keyof CloseReading, unknown>>
		): ValidationResult => {
			try {
				validateField(question.phrase, 'string', 'phrase')
				validateField(question.textBody, 'string', 'textBody')
				validateField(question.grading, 'object', 'grading')
				validateField(question.matchType, CLOSE_READING.MATCH_TYPE, 'matchType')
				validateField(
					question.availableAnnotationTools,
					'array-non-empty',
					'availableAnnotationTools'
				)
				if (question.annotationLimit != null) {
					validateField(question.annotationLimit, 'number', 'annotationLimit')
				}
				if (question.requiredAnnotations != null) {
					validateField(
						question.requiredAnnotations,
						'number',
						'requiredAnnotations'
					)
					const requiredAnnotations = question.requiredAnnotations as number
					const annotationLimit = question.annotationLimit as number | null

					if (
						annotationLimit != null &&
						requiredAnnotations > annotationLimit
					) {
						throw new _ValidationError(
							`requiredAnnotations must be less than or equal to annotationLimit`,
							'requiredAnnotations'
						)
					}
				}

				const grading = question.grading as CloseReading['grading']
				validateField(grading.type, CLOSE_READING.GRADING, 'grading.type')
				const doNotGrade = grading.type === CLOSE_READING.GRADING.NONE
				validateField(
					question.targetAnnotations,
					doNotGrade ? 'array' : 'array-non-empty',
					'targetAnnotations'
				)
				const textBody = question.textBody as string
				const annotationTools = question.availableAnnotationTools as Record<
					string,
					unknown
				>[]

				if (grading.type === CLOSE_READING.GRADING.PASS_FAIL) {
					validateField(
						grading.requiredScore,
						'number',
						'grading.requiredScore'
					)
				}
				const targetAnnotations = question.targetAnnotations as Record<
					string,
					unknown
				>[]
				const availableAnnotationTools: Partial<Record<
					keyof typeof CLOSE_READING.ANNOTATION_TOOL,
					Array<keyof typeof CLOSE_READING.COLOR>
				>> = {}
				// Validate annotation tools
				annotationTools.forEach((tool: Record<string, unknown>, index) => {
					validateField(tool, 'object', `availableAnnotationTools[${index}]`)
					validateField(
						tool.type,
						CLOSE_READING.ANNOTATION_TOOL,
						`availableAnnotationTools[${index}].type`
					)
					const toolType = tool.type as keyof typeof CLOSE_READING.ANNOTATION_TOOL
					if (tool.colors) {
						validateField(
							tool.colors,
							'array-non-empty',
							`availableAnnotationTools[${index}].colors`
						)
						const colors = tool.colors as string[]
						colors.map((color, colorIndex) =>
							validateField(
								color,
								CLOSE_READING.COLOR,
								`availableAnnotationTools[${index}].colors[${colorIndex}]`
							)
						)
						availableAnnotationTools[toolType] = colors as Array<
							keyof typeof CLOSE_READING.COLOR
						>
					}
				})
				const availableColors = new Set(
					Object.values(availableAnnotationTools).flat()
				)

				// Validate target annotations
				targetAnnotations.forEach((annotation, index) => {
					validateField(annotation, 'object', `targetAnnotations[${index}]`)
					validateField(
						annotation.score,
						'number',
						`targetAnnotations[${index}].score`
					)
					validateField(
						annotation.required,
						'boolean',
						`targetAnnotations[${index}].required`
					)
					validateField(
						annotation.range,
						'object',
						`targetAnnotations[${index}].range`
					)
					validateField(
						annotation.buffer,
						'object',
						`targetAnnotations[${index}].buffer`
					)
					validateField(
						annotation.style,
						'object',
						`targetAnnotations[${index}].style`
					)

					const range = annotation.range as Record<string, unknown>
					const buffer = annotation.buffer as Record<string, unknown>

					validateField(
						range.start,
						'number',
						`targetAnnotations[${index}].range.start`
					)
					validateField(
						range.end,
						'number',
						`targetAnnotations[${index}].range.end`
					)
					validateField(
						buffer.start,
						'number',
						`targetAnnotations[${index}].range.start`
					)
					validateField(
						buffer.end,
						'number',
						`targetAnnotations[${index}].range.end`
					)

					const rangeStart = range.start as number
					const rangeEnd = range.end as number
					const bufferStart = buffer.start as number
					const bufferEnd = buffer.end as number

					// Make sure target annotations fit in text body
					if (
						textBody[rangeStart] == null ||
						textBody[rangeEnd] == null ||
						textBody[bufferStart] == null ||
						textBody[bufferEnd] == null
					) {
						throw new _ValidationError(
							`range values are out of bounds of text`,
							`targetAnnotations[${index}].range`
						)
					}
					if (rangeStart >= rangeEnd) {
						throw new _ValidationError(
							`range start occurs after range end`,
							`targetAnnotations[${index}].range`
						)
					}
					if (bufferStart >= bufferEnd) {
						throw new _ValidationError(
							`buffer start occurs after buffer end`,
							`targetAnnotations[${index}].buffer`
						)
					}
					if (rangeStart < bufferStart || rangeEnd > bufferEnd) {
						throw new _ValidationError(
							`range must be within buffer`,
							`targetAnnotations[${index}].range`
						)
					}
					const style = annotation.style as Record<string, unknown>
					if (style.tool) {
						validateField(
							style.tool,
							CLOSE_READING.ANNOTATION_TOOL,
							`targetAnnotations[${index}].style.tool`
						)
						if (
							availableAnnotationTools[
								style.tool as keyof typeof CLOSE_READING.ANNOTATION_TOOL
							]
						) {
							const colors =
								availableAnnotationTools[
									style.tool as keyof typeof CLOSE_READING.ANNOTATION_TOOL
								]
							if (
								style.color &&
								(!colors ||
									!colors.includes(
										style.color as keyof typeof CLOSE_READING.COLOR
									))
							) {
								throw new _ValidationError(
									`color is not available for tool`,
									`targetAnnotations[${index}].style.color`
								)
							}
						} else {
							throw new _ValidationError(
								`tool is not available`,
								`targetAnnotations[${index}].style.tool`
							)
						}
					}
					if (style.color) {
						validateField(
							style.color,
							CLOSE_READING.COLOR,
							`targetAnnotations[${index}].style.color`
						)

						if (
							!availableColors.has(
								style.color as keyof typeof CLOSE_READING.COLOR
							)
						) {
							throw new _ValidationError(
								`target color is not available for annotation`,
								`targetAnnotations[${index}].style.color`
							)
						}
					}
				})
			} catch (error) {
				if (error instanceof _ValidationError) {
					return { error: error.message, field: error.field }
				}
				throw error
			}
		},
		getClassScores: (
			question: ClientQuestion,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			_answers: StudentAnswer[]
		): ClassScore | void => {
			// TODO: Eventually evaluate class scores for a given annotation
			if (question.type === QuestionVariant.CLOSE_READING) {
				return undefined
			}
		},
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		getCorrectAnswer: (_question: ClientQuestion): StudentAnswer | void => {
			// TODO: Eventually return the correct answer for a close reading question
		},
		updateMedia: (question: Question): Question => {
			return question // there is no additional media to update for a close reading question
		},
	},
	[QuestionVariant.CLOZE_DROPDOWN]: {
		getSchema: (
			Schema: typeof MongooseSchema
		): MongooseSchema<Omit<ClozeDropdown, 'type'>> => {
			const DropdownSchema = new Schema(
				{
					templateResponseKey: required(String),
					choices: required([getQuestionChoiceSchema(Schema)]),
				},
				{ _id: false }
			)
			return new Schema({
				template: required(String),
				dropdowns: requiredNonEmptyArray(
					DropdownSchema,
					`Question must contain at least one drop-down`
				),
			})
		},
		getClassScores: (
			question: ClientQuestion,
			answers: StudentAnswer[]
		): ClassScore | void => {
			if (question.type === QuestionVariant.CLOZE_DROPDOWN) {
				return getClassScoresWithTemplateIdQuestion(question, answers)
			}
		},
		getCorrectAnswer: (question: ClientQuestion): StudentAnswer | void => {
			if (question.type !== QuestionVariant.CLOZE_DROPDOWN) {
				return
			}
			const correctAnswers: { [templateKey: string]: string } = {}
			question.dropdowns.forEach(dropDown => {
				const correctChoice = dropDown.choices.find(choice => choice.answer)
					?._id
				if (!correctChoice) {
					return
				}
				correctAnswers[dropDown.templateResponseKey] = String(correctChoice)
			})
			if (Object.keys(correctAnswers).length !== question.dropdowns.length) {
				return
			}
			return {
				type: QuestionVariant.CLOZE_DROPDOWN,
				value: correctAnswers,
			}
		},
		updateMedia: updateMediaForQuestionWithDropdowns,
		validate: (
			question: Partial<Record<keyof ClozeDropdown, unknown>>
		): ValidationResult => {
			if (!question.template) {
				return { error: 'missing text', field: 'template' }
			}
			if (!isString(question.template)) {
				return { error: 'incorrect format', field: 'template' }
			}
			const template: string = question.template
			const responses = new Set(getTemplateKeys(template))
			if (!responses.size) {
				return { error: 'at least one dropdown is required', field: 'template' }
			}
			if (
				!Array.isArray(question.dropdowns) ||
				question.dropdowns.length === 0
			) {
				return {
					error: 'at least one dropdown is required',
					field: 'dropdowns',
				}
			}
			for (const dropdown of question.dropdowns) {
				if (
					!dropdown.templateResponseKey ||
					!isString(dropdown.templateResponseKey)
				) {
					return {
						error: 'a dropdown key is badly formatted',
						field: 'dropdowns',
					}
				}
				if (!responses.has(dropdown.templateResponseKey)) {
					return {
						error: 'a dropdown created is no longer in the phrase',
						field: 'dropdowns',
					}
				}
				responses.delete(dropdown.templateResponseKey)
				// Validate choices (only accept 1 answer from each dropdown)
				const choiceValidation = validateChoices(dropdown.choices)
				if (choiceValidation.error) {
					return {
						error: `${choiceValidation.error} for dropdown with key ${dropdown.templateResponseKey}`,
						field: 'dropdowns',
					}
				} else if (choiceValidation.answerCount !== 1) {
					return {
						error: `too many answers marked as correct for dropdown with key ${dropdown.templateResponseKey}`,
						field: 'dropdowns',
					}
				}
			}
			if (responses.size > 0) {
				// The template is referencing a non-existent drop down.
				return {
					error: 'phrase references a dropdown that does not exist',
					field: 'template',
				}
			}
		},
		grade: (question: Question, answer: StudentAnswer): number | undefined => {
			if (answer.type !== QuestionVariant.CLOZE_DROPDOWN) return
			if (!isLikeObject<Record<string, unknown>>(answer.value)) return
			if (question.type !== QuestionVariant.CLOZE_DROPDOWN) return
			return gradeDropdowns(
				question.dropdowns,
				dropdown => dropdown.templateResponseKey,
				answer
			)
		},
	},
	[QuestionVariant.FREE_RESPONSE]: {
		getSchema: (
			Schema: typeof MongooseSchema
		): MongooseSchema<Omit<FreeResponse, 'type'>> => {
			return new Schema({
				phrase: required(String),
				answer: String,
			})
		},
		grade: (): null => {
			// We don't have the AI to grade a free response question.
			return null
		},
		updateMedia: (question: Question): Question => {
			return question
		},
		getClassScores: (): void => {
			return
		},
		getCorrectAnswer: (question: ClientQuestion): StudentAnswer | void => {
			if (question.type !== QuestionVariant.FREE_RESPONSE) {
				return
			}
			return {
				type: QuestionVariant.FREE_RESPONSE,
				value: question.answer || '',
			}
		},
		validate: (
			question: Partial<Record<keyof FreeResponse, unknown>>
		): ValidationResult => {
			if (!question.phrase) {
				return {
					error: 'missing text',
					field: 'phrase',
				}
			}
			if (!isString(question.phrase)) {
				return { error: 'incorrectly formatted', field: 'phrase' }
			}
			if (question.answer != null && !isString(question.answer)) {
				return { error: 'incorrectly formatted', field: 'answer' }
			}
		},
	},
	[QuestionVariant.IMAGE_LABELING]: {
		getSchema: (
			Schema: typeof MongooseSchema
		): MongooseSchema<Omit<ImageLabeling, 'type'>> => {
			const DropdownSchema = new Schema(
				{
					x: required(Number),
					y: required(Number),
					choices: requiredNonEmptyArray(
						getQuestionChoiceSchema(Schema),
						'A dropdown is missing options'
					),
				},
				{ _id: false }
			)
			return new Schema({
				media: requiredNonEmptyArray(
					getMediaSchema(Schema),
					`Question must contain an image`
				),
				phrase: required(String),
				imageProperties: required(
					new Schema(
						{
							width: required(Number),
							height: required(Number),
						},
						{ _id: false }
					),
					{ default: { width: 550, height: 250 } }
				),
				dropdowns: requiredNonEmptyArray(
					DropdownSchema,
					`Question must contain at least one drop-down`
				),
			})
		},
		grade: (question: Question, answer: StudentAnswer): number | undefined => {
			if (answer.type !== QuestionVariant.IMAGE_LABELING) return
			if (!isLikeObject<Record<string, unknown>>(answer.value)) return
			if (question.type !== QuestionVariant.IMAGE_LABELING) return
			return gradeDropdowns(question.dropdowns, getImageDropdownKey, answer)
		},
		getClassScores: (
			question: ClientQuestion,
			answers: StudentAnswer[]
		): ClassScore | void => {
			if (question.type === QuestionVariant.IMAGE_LABELING) {
				return getClassScoresWithTemplateIdQuestion(question, answers)
			}
		},
		updateMedia: updateMediaForQuestionWithDropdowns,
		getCorrectAnswer: (question: ClientQuestion): StudentAnswer | void => {
			if (question.type !== QuestionVariant.IMAGE_LABELING) {
				return
			}
			const correctAnswers: { [templateKey: string]: string } = {}
			question.dropdowns.forEach(dropDown => {
				const correctChoice = dropDown.choices.find(choice => choice.answer)
					?._id
				if (correctChoice) {
					return
				}
				correctAnswers[getImageDropdownKey(dropDown)] = String(correctChoice)
			})
			if (Object.keys(correctAnswers).length !== question.dropdowns.length) {
				return
			}
			return {
				type: QuestionVariant.IMAGE_LABELING,
				value: correctAnswers,
			}
		},
		validate: (
			question: Partial<Record<keyof ImageLabeling, unknown>>
		): ValidationResult => {
			if (!question.phrase) {
				return {
					error: 'missing text',
					field: 'phrase',
				}
			}
			if (!isString(question.phrase)) {
				return { error: 'incorrectly formatted', field: 'phrase' }
			}
			if (
				question.media &&
				(!Array.isArray(question.media) ||
					question.media[0].type !== MediaType.IMAGE)
			) {
				return { error: 'unknown media format', field: 'media' }
			}
			if (
				!question.media ||
				(Array.isArray(question.media) && question.media.length === 0)
			) {
				return { error: 'missing a valid image', field: 'media' }
			}
			// There must be one and only one IMAGE on the question.
			// TODO: When we allow for more media types, the logic here will need to change since the array size could be larger than 1.
			if (Array.isArray(question.media) && question.media.length > 1) {
				return {
					error: 'only one media image is allowed',
					field: 'media',
				}
			}
			// Validate image properties
			const _imageProperties = question.imageProperties as
				| {
						width?: unknown
						height?: unknown
				  }
				| undefined
			if (
				!isLikeObject(_imageProperties) ||
				!_imageProperties.hasOwnProperty('width') ||
				!_imageProperties.hasOwnProperty('height') ||
				typeof _imageProperties.width !== 'number' ||
				typeof _imageProperties.height !== 'number' ||
				_imageProperties.width <= 0 ||
				_imageProperties.height <= 0
			) {
				return {
					error: 'failed to validate image properties',
					field: 'imageProperties',
				}
			}
			const imageProperties = _imageProperties as {
				width: number
				height: number
			}
			// Validate dropdowns
			if (
				!Array.isArray(question.dropdowns) ||
				question.dropdowns.length === 0
			) {
				return {
					error: 'at least one dropdown is required',
					field: 'dropdowns',
				}
			}
			const usedCoordinatePairs = new Set()
			let dropdownNumber = 0
			for (const dropdown of question.dropdowns) {
				dropdownNumber += 1
				// Validate each dropdown
				if (typeof dropdown !== 'object' || !dropdown) {
					return {
						error: 'a dropdown is incorrectly formatted',
						field: 'dropdowns',
					}
				}
				const coordinatePairStr = getImageDropdownKey(dropdown)
				if (usedCoordinatePairs.has(coordinatePairStr)) {
					return {
						error: `dropdown ${dropdownNumber} is in the same location on the image as another dropdown`,
						field: 'dropdowns',
					}
				}
				if (
					typeof dropdown.x !== 'number' ||
					typeof dropdown.y !== 'number' ||
					// Check to make sure coordinates are within the imageProperty width and height
					!(dropdown.x >= 0 && dropdown.x <= imageProperties.width) ||
					!(dropdown.y >= 0 && dropdown.y <= imageProperties.height)
				) {
					return {
						error: `the coordinates for dropdown ${dropdownNumber} are outside of the image`,
						field: 'dropdowns',
					}
				}
				usedCoordinatePairs.add(coordinatePairStr)

				// Validate choices (only accept 1 answer from each dropdown)
				const validatedChoices = validateChoices(dropdown.choices)
				if (validatedChoices.error) {
					return {
						error: `${validatedChoices.error} for dropdown ${dropdownNumber}`,
						field: 'dropdowns',
					}
				} else if (validatedChoices.answerCount !== 1) {
					return {
						error: `too many answers marked as correct for dropdown ${dropdownNumber}`,
						field: 'dropdowns',
					}
				}
			}
		},
	},
	[QuestionVariant.MULTIPLE_CHOICE]: {
		getSchema: (
			Schema: typeof MongooseSchema
		): MongooseSchema<Omit<MultipleChoice, 'type'>> => {
			return new Schema({
				phrase: required(String),
				choices: required([getQuestionChoiceSchema(Schema)]),
			})
		},
		grade: (question: Question, answer: StudentAnswer): number | undefined => {
			if (question.type !== QuestionVariant.MULTIPLE_CHOICE) return undefined
			if (answer.type !== QuestionVariant.MULTIPLE_CHOICE) return undefined
			const studentChoice = question.choices.find(choice => {
				return String(choice._id) === String(answer.value)
			})
			return studentChoice ? (studentChoice.answer ? 1 : 0) : undefined
		},
		updateMedia: (
			question: Question,
			callback: (media: MediaObj) => MediaObj
		): Question => {
			if (question.type !== QuestionVariant.MULTIPLE_CHOICE) return question
			question.choices = updateQuestionChoiceMedia(question.choices, callback)
			return question
		},
		getClassScores: (
			question: ClientQuestion,
			answers: StudentAnswer[]
		): ClassScore | void => {
			if (question.type === QuestionVariant.MULTIPLE_CHOICE) {
				return getClassScoresFromChoices(question, answers)
			}
		},
		getCorrectAnswer: (question: ClientQuestion): StudentAnswer | void => {
			if (question.type !== QuestionVariant.MULTIPLE_CHOICE) {
				return
			}
			const correctChoice = question.choices.find(({ answer }) => answer)?._id
			if (!correctChoice) {
				return
			}
			return {
				type: QuestionVariant.MULTIPLE_CHOICE,
				value: String(correctChoice),
			}
		},
		validate: (
			question: Partial<Record<keyof MultipleChoice, unknown>>
		): ValidationResult => {
			if (!question.phrase) {
				return {
					error: 'missing text',
					field: 'phrase',
				}
			}
			if (!isString(question.phrase))
				return {
					error: `incorrectly formatted`,
					field: 'phrase',
				}
			const result = validateChoices(question.choices)
			if (result.error) {
				return { error: result.error, field: 'choices' }
			} else if (result.answerCount !== 1) {
				return { error: `too many answers marked as correct`, field: 'choices' }
			}
		},
	},
	[QuestionVariant.MULTIPLE_SELECT]: {
		getSchema: (
			Schema: typeof MongooseSchema
		): MongooseSchema<Omit<MultipleSelect, 'type'>> => {
			return new Schema({
				phrase: required(String),
				choices: required([getQuestionChoiceSchema(Schema)]),
			})
		},
		grade: (question: Question, answer: StudentAnswer): number | undefined => {
			// TODO: Allow different grading tactics on a multi-select question
			if (answer.type !== QuestionVariant.MULTIPLE_SELECT) return undefined
			if (!Array.isArray(answer.value)) return
			if (question.type !== QuestionVariant.MULTIPLE_SELECT) return undefined
			const correctChoiceIds = new Set()
			const answerValue = answer.value
			const choiceMap: {
				[choiceId: string]: QuestionChoice
			} = question.choices.reduce((map, choice) => {
				if (choice.answer === true) {
					correctChoiceIds.add(String(choice._id))
				}
				return { ...map, [String(choice._id)]: choice }
			}, {})
			if (
				!Array.isArray(answerValue) ||
				answerValue.some(choiceId => !choiceMap[String(choiceId)])
			) {
				// a badly formatted answer
				return undefined
			}
			// Make sure every answer that the student made is a unique, correct choice
			for (const _studentChoiceId of answerValue) {
				const studentChoiceId = String(_studentChoiceId)
				if (correctChoiceIds.has(studentChoiceId)) {
					// Student chose correctly: we mark the choice off by removing the id from the set.
					correctChoiceIds.delete(studentChoiceId)
				} else {
					return 0
				}
			}
			// Make sure the student chose all the correct choices and didn't miss one.
			return correctChoiceIds.size === 0 ? 1 : 0
		},
		updateMedia: (
			question: Question,
			callback: (media: MediaObj) => MediaObj
		): Question => {
			if (question.type !== QuestionVariant.MULTIPLE_SELECT) return question
			question.choices = updateQuestionChoiceMedia(question.choices, callback)
			return question
		},
		getClassScores: (
			question: ClientQuestion,
			answers: StudentAnswer[]
		): ClassScore | void => {
			if (question.type === QuestionVariant.MULTIPLE_SELECT) {
				return getClassScoresFromChoices(question, answers)
			}
		},
		getCorrectAnswer: (question: ClientQuestion): StudentAnswer | void => {
			if (question.type !== QuestionVariant.MULTIPLE_SELECT) {
				return
			}
			const correctChoices = question.choices
				.filter(({ answer }) => answer)
				.map(({ _id }) => String(_id))
			if (!correctChoices.length) {
				return
			}
			return {
				type: QuestionVariant.MULTIPLE_SELECT,
				value: correctChoices,
			}
		},
		validate: (
			question: Partial<Record<keyof MultipleChoice, unknown>>
		): ValidationResult => {
			if (!question.phrase) {
				return { error: 'missing text', field: 'phrase' }
			}
			if (!isString(question.phrase))
				return { error: `incorrectly formatted`, field: 'phrase' }
			const result = validateChoices(question.choices)
			if (result.error) {
				return { error: result.error, field: 'choices' }
			}
		},
	},
}

/**
 * Get the key that references a dropdown on an image.
 * The key is derived from the dropdown location.
 * @param {{x: number, y: number}} dropdown the dropdown located on an image
 * @returns {string} the key as a string
 */
export function getImageDropdownKey(dropdown: {
	x: number
	y: number
}): string {
	return `${dropdown.x}-${dropdown.y}`
}

/**
 * Grades a list of question drop-downs to determine if the student answer provided contains all
 * the correct answers for each drop down.
 * @param {T[]} dropdowns
 * @param {~callback} getIdentifierForDropdown gets the id of a dropdown
 * @param {Record<string, unknown>} answer
 * @returns {boolean | undefined} undefined if any of the students choices for a dropdown were invalid.
 */
function gradeDropdowns<T extends { choices: QuestionChoice[] }>(
	dropdowns: T[],
	getIdentifierForDropdown: (dropdown: T) => string,
	answer: DropdownAnswer
): number | undefined {
	const answerValue = answer.value
	const totalCorrect = dropdowns.reduce(
		(totalCorrect: number | undefined, dropdown) => {
			if (totalCorrect === undefined) return totalCorrect
			const studentChoiceId = answerValue[getIdentifierForDropdown(dropdown)]
			if (!studentChoiceId) {
				return undefined
			}
			const studentChoice = dropdown.choices.find(choice => {
				return String(choice._id) === String(studentChoiceId)
			})
			if (!studentChoice) {
				return undefined
			}
			return totalCorrect + (studentChoice.answer ? 1 : 0)
		},
		0
	)
	if (totalCorrect === undefined || dropdowns.length === 0) return undefined
	return totalCorrect / dropdowns.length
}

/**
 * Updates the media objects found on a set of question choices
 * @param {QuestionChoice[]} choices
 * @param {~callback} callback
 * @returns {QuestionChoices[]}
 */
function updateQuestionChoiceMedia(
	choices: QuestionChoice[],
	callback: (media: MediaObj) => MediaObj
): QuestionChoice[] {
	return choices.map(choice => {
		if (!choice.media) return choice
		return {
			...choice,
			media: choice.media.map(mediaItem => callback(mediaItem)),
		}
	})
}

/**
 * Update the media objects found on a question that has drop-downs.
 * @param {Question} question
 * @param {~callback} callback callback function used to update a media object
 * @returns
 */
function updateMediaForQuestionWithDropdowns<T extends Question>(
	question: T,
	callback: (media: MediaObj) => MediaObj
): T {
	if (
		question.type !== QuestionVariant.CLOZE_DROPDOWN &&
		question.type !== QuestionVariant.IMAGE_LABELING
	) {
		return question
	}
	return {
		...question,
		dropdowns: question.dropdowns.map(dropdown => ({
			...dropdown,
			choices: updateQuestionChoiceMedia(dropdown.choices, callback),
		})),
	}
}

/**
 * Validates the choices array on a question
 * Returns an object containing the 'answerCount'
 * which is the total correct answers provided from the list of choices
 * @param {unknown} choices
 * @returns {{answerCount: number} | undefined} if undefined is returned, the question is valid.
 */
function validateChoices(
	choices: unknown
): { answerCount?: number; error?: string } {
	let answerCount = 0
	let choiceNumber = 0
	if (choices && Array.isArray(choices) && choices.length > 0) {
		for (const choice of choices) {
			choiceNumber += 1
			if (!choice) return { error: `choice ${choiceNumber} is undefined` }
			let someMediaExists = false
			if (choice.media && Array.isArray(choice.media)) {
				someMediaExists = choice.media?.find(Boolean)
			}
			if (someMediaExists && !validateMedia(choice.media))
				return { error: `media is badly formatted for choice ${choiceNumber}` }
			if (!choice.text && !someMediaExists)
				return { error: `no media or text exists for choice ${choiceNumber}` }
			if (choice.text && !isString(choice.text)) {
				return {
					error: `text for choice ${choiceNumber} is incorrectly formatted`,
				}
			}
			if (choice.answer) answerCount++
		}
	}
	if (choiceNumber === 0) {
		return { error: 'at least one choice is required' }
	}
	if (answerCount) {
		return { answerCount }
	}
	return { error: 'no choice was marked as correct' }
}
class _ValidationError extends Error {
	field: string
	constructor(message: string, field: string) {
		super(message)
		this.field = field
	}
}

/**
 * Validates a field based on its type and value. Checks if its missing or an incorrect type.
 * @param {unknown} value
 * @param {string | Record<string, unknown>} type
 * @param {string} path
 * @returns {ValidationResult} an error if fails or undefined if passes
 */
function validateField(
	value: unknown,
	type:
		| 'array-non-empty'
		| 'array'
		| 'string'
		| 'object'
		| 'number'
		| 'boolean'
		| Record<string, unknown>,
	path: string
): void {
	if (value == null) {
		throw new _ValidationError(`missing field`, path)
	} else {
		if (typeof type === 'string' && type.includes('array')) {
			if (!Array.isArray(value)) {
				throw new _ValidationError(`expected array`, path)
			}
			if (type === 'array-non-empty' && value.length == 0) {
				throw new _ValidationError(`array must not be empty`, path)
			}
		} else if (typeof type === 'string') {
			if (typeof value !== type) {
				throw new _ValidationError(`expected ${type}`, path)
			}
			return
		} else if (!Object.values(type).includes(value)) {
			throw new _ValidationError(`wrong enum value`, path)
		}
	}
}

let _QuestionChoiceSchema: null | MongooseSchema<QuestionChoice> = null
/**
 * Gets schema for the a question's choices.
 * @param {typeof MongooseSchema} Schema
 * @returns { MongooseSchema<QuestionChoice>} a schema for a question choice
 */
function getQuestionChoiceSchema(
	Schema: typeof MongooseSchema
): MongooseSchema<QuestionChoice> {
	if (_QuestionChoiceSchema) {
		return _QuestionChoiceSchema
	}
	_QuestionChoiceSchema = new Schema({
		text: {
			type: String,
			required: function(): boolean {
				return (
					!((this as unknown) as QuestionChoice).media ||
					((this as unknown) as QuestionChoice).media?.length == 0
				)
			},
		},
		media: {
			type: [getMediaSchema(Schema)],
			// only require media if there is no text
			required: function(): boolean {
				return !((this as unknown) as QuestionChoice).text
			},
		},
		answer: required(Boolean),
	})
	return _QuestionChoiceSchema
}

/**
 * getClassScoresWithTemplateIdQuestion - get the class scores for the questions which use templateIds in their answers
 *
 * @param {ClozeDropdown<ClientFields> | ImageLabeling<ClientFields>} question - the question to get the class score for
 * @param {StudentAnswer[]} answers - the student answers for the given question
 *
 * @returns ClassScore | void - get the class scores for the question (null/undefined) if could not generate the class scores
 */
function getClassScoresWithTemplateIdQuestion(
	question: ClozeDropdown<ClientFields> | ImageLabeling<ClientFields>,
	_answers: StudentAnswer[]
): ClassScore | void {
	if (_answers.some(answer => answer.type !== question.type)) {
		return
	}
	// We've ensured that the only answers here are cloze dropdown and image labeling
	// This assumption is safe as long as the answer types for questions cloze dropdown and image labeling are always dropdown answers
	const answers: DropdownAnswer[] = _answers as DropdownAnswer[]
	const answerCounts: {
		[templateId: string]: { [option: string]: number }
	} = {}
	const templateCount: { [templateId: string]: number } = {}

	answers.forEach(studentAnswer => {
		const selectedOptions = studentAnswer.value
		if (typeof selectedOptions !== 'object' || Array.isArray(selectedOptions)) {
			return
		}
		Object.keys(selectedOptions).forEach(templateId => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const option = selectedOptions[templateId]!
			templateCount[templateId] = (templateCount[templateId] || 0) + 1

			answerCounts[templateId] = answerCounts[templateId] || {}

			// all of these non-null assertions are safe as we have already checked that the objects exist
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			answerCounts[templateId]![option] =
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				(answerCounts[templateId]![option] || 0) + 1
		})
	})

	return {
		type: question.type,
		optionScores: Object.keys(answerCounts).map((templateId: string) => ({
			templateResponsekey: templateId,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			choices: Object.keys(answerCounts[templateId]!).map(_id => ({
				_id,
				score:
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					answerCounts[templateId]![_id]! / (templateCount[templateId] || 1),
			})),
		})),
	}
}

/**
 * getClassScoresFromChoices - get the class scores for the questions which use choices (without template ids) in their answers
 *
 * @param {MultipleChoice<ClientFields> | MultipleSelect<ClientFields>} question - the question to get the class score for
 * @param {StudentAnswer[]} answers - the student answers for the given question
 *
 * @returns ClassScore | void - get the class scores for the question (null/undefined) if could not generate the class scores
 */
function getClassScoresFromChoices(
	question: MultipleChoice<ClientFields> | MultipleSelect<ClientFields>,
	answers: StudentAnswer[]
): ClassScore | void {
	const answersOfSameQuestionType = answers.filter(
		answer => answer.type === question.type
	)

	const optionCounts: { [option: string]: number } = {}
	answersOfSameQuestionType.forEach((studentAnswer: StudentAnswer) => {
		const option = studentAnswer.value
		if (typeof option !== 'string' && !Array.isArray(option)) {
			return
		}
		const options = Array.isArray(option) ? option : [option]
		options.forEach(option => {
			if (typeof option !== 'string') {
				return
			}
			optionCounts[option] = (optionCounts[option] || 0) + 1
		})
	})
	return {
		type: question.type,
		optionScores: Object.keys(optionCounts).map((answerId: string) => ({
			_id: answerId,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			score: optionCounts[answerId]! / (answers.length || 1),
		})),
	}
}
